import {useCallback, useEffect, useRef} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {toastAlert} from '~library/atoms/Toast';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';
import {VOLUNTEER_SIGNUP} from '~/common/fabric/constants';
import {validatePhoneNumber, validate_name_or_email as validNameOrEmail} from '~/common/utils';
import {
  selectIsPhoneRequired,
  selectSelectedSignups,
  selectSheetId,
  selectSignupsPendingDeletion,
  selectSignupQuestions,
  selectSheetType,
} from '~/SignUpSheets/reducers/sheet/selectors';
import {
  useLazyGetSheetQuery,
  useRemoveSignupMutation,
  useSaveSignupsMutation,
  useUpdateSignupsMutation,
} from '~/SignUpSheets/api/sheet';
import {
  SET_SELECTED_SIGNUPS,
  SET_SHEET,
  UPDATE_SIGNUPS,
} from '~/SignUpSheets/reducers/sheet/constants';
import {selectIsDateTimeBlueprint} from '~/SignUpSheets/reducers/blueprint/selectors';
import {parseErrors} from '~/SignUpSheets/utils/validation';

const handleSheetUpdateError = (message) => {
  toastAlert(message || 'Failed to update sheet', {
    icon: <AlertLineIcon ratio={1.5} />,
    type: 'error',
    position: 'top-right',
  });
  return false;
};

export const useSaveOrUpdateSignups = ({
  volunteerName,
  volunteerEmail,
  volunteerPhone,
  isEditing,
  showPhoneField,
  setNameError,
  setEmailError,
  setPhoneError,
  setSignupError,
  setQuestionError,
  scope = 'signup', // 'signup' or 'view'
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const sheetId = useSelector(selectSheetId);
  const [getSheet] = useLazyGetSheetQuery();
  const [saveSignups] = useSaveSignupsMutation();
  const [updateSignups] = useUpdateSignupsMutation();
  const [removeSignup] = useRemoveSignupMutation();
  const selectedSignups = useSelector(selectSelectedSignups, shallowEqual);
  const signupsPendingDeletion = useSelector(selectSignupsPendingDeletion, shallowEqual);
  const isDatetimeType = useSelector(selectIsDateTimeBlueprint);
  const userId = useSelector((state) => state.user.userId);
  const isPhoneRequired = useSelector(selectIsPhoneRequired);
  const questions = useSelector(selectSignupQuestions);
  const sheetType = useSelector(selectSheetType);
  const hasAttemptedSave = useRef(false);

  const validate = () => {
    let hasError = false;
    if (validNameOrEmail('name', volunteerName)) {
      hasError = true;
      setNameError('Please enter your name');
    } else {
      setNameError('');
    }
    if (validNameOrEmail('email', volunteerEmail)) {
      hasError = true;
      setEmailError('Please enter a valid email');
    } else {
      setEmailError('');
    }
    if (showPhoneField && validatePhoneNumber(volunteerPhone, isPhoneRequired)) {
      hasError = true;
      setPhoneError('Please enter a valid phone number');
    } else {
      setPhoneError('');
    }

    const requiredQuestions = questions.filter(
      (q) => q.is_required && q.display_on_signup && q.applicable_to?.includes(sheetType)
    );

    if (requiredQuestions?.length) {
      const hasEmptyRequiredAnswers = selectedSignups.some((signup) =>
        requiredQuestions.some((question) => {
          const answer = signup.answers?.find((a) => a.question_id === question.uuid);
          return !answer?.answer;
        })
      );

      if (hasEmptyRequiredAnswers) {
        hasError = true;
        setQuestionError(
          'This question is required by your organizer. If it does not apply to you, add N/A'
        );
      } else {
        setQuestionError('');
      }
    }

    return hasError;
  };

  const clearSelectedSignups = useCallback(() => {
    dispatch({type: SET_SELECTED_SIGNUPS, payload: []});
  }, [dispatch]);

  const handleSave = useCallback(
    async (optionsToSubmit) => {
      hasAttemptedSave.current = true;
      if (scope === 'signup' && validate()) throw new Error('validation failed');

      const signupsData = optionsToSubmit.map((signup) => ({
        signup_option: !isDatetimeType ? signup.signup_option : undefined,
        slot_id: signup.slot_id,
        user_id: userId,
        name: volunteerName,
        email: volunteerEmail,
        phone: volunteerPhone,
        comment: signup.comment,
        answers: signup.answers,
      }));

      dispatch({
        type: VOLUNTEER_SIGNUP,
        payload: {volunteerEmail, volunteerPhone, volunteerName},
      });

      const results = await saveSignups({
        id: sheetId,
        signups: signupsData,
      });

      if (results.error) {
        throw results.error;
      }

      const selectedSignupsLookup = selectedSignups.reduce((lookup, signup, index) => {
        const key = `${isDatetimeType ? signup.slot_id : signup.signup_option}-${index}`;
        return {
          ...lookup,
          [key]: signup,
        };
      }, {});
      const matchingSignups = results.data.data
        .map((resultSignup, index) => {
          const key = `${isDatetimeType ? resultSignup.slot_id : resultSignup.signup_option}-${index}`;
          const matchingSelectedSignup = selectedSignupsLookup[key];

          if (matchingSelectedSignup && volunteerEmail === resultSignup.email) {
            return {
              ...resultSignup,
              oldId: matchingSelectedSignup.id,
            };
          }

          return resultSignup;
        })
        .filter((resultSignup) => resultSignup.oldId);

      dispatch({type: UPDATE_SIGNUPS, payload: matchingSignups});
      const updatedSheet = await getSheet(sheetId);
      dispatch({type: SET_SHEET, payload: updatedSheet.data.data});
      return results;
    },
    [
      dispatch,
      saveSignups,
      validate,
      volunteerName,
      volunteerEmail,
      volunteerPhone,
      selectedSignups,
      getSheet,
      sheetId,
      scope,
    ]
  );

  const handleUpdateSignups = useCallback(
    async (items) => {
      const result = await updateSignups({
        id: sheetId,
        signups: items,
      });

      if (result.error?.status === 422) {
        const {savingErrors, validationErrors} = parseErrors(
          result.error.data.error.data?.detail ?? []
        );
        handleSheetUpdateError(savingErrors?.[0] || validationErrors?.[0].msg);
        return false;
      }

      const updatedItems = result.data.data.map((item) => ({
        ...item,
        comment: item.comment,
        answers: item.answers,
      }));

      dispatch({type: UPDATE_SIGNUPS, payload: updatedItems});

      if (result.error) {
        throw result.error;
      }
      return true;
    },
    [dispatch, updateSignups, sheetId]
  );

  const onSave = useCallback(
    async (signups = selectedSignups ?? []) => {
      try {
        let newUserId;

        const updatedSignups = signups
          .filter((s) => s.id)
          .map((s) => ({...s, email: volunteerEmail}));
        const newSignups = signups.filter((s) => !s.id);

        if (updatedSignups.length > 0) {
          // Some signups have been updated; save updates
          const success = await handleUpdateSignups(updatedSignups);
          if (!success) {
            return;
          }
        }
        if (newSignups.length > 0) {
          // Some new signups; save those
          const saveResult = await handleSave(newSignups);
          newUserId = saveResult?.data?.data[0]?.user_id;
        }
        for await (const signupToDelete of signupsPendingDeletion) {
          // Some signups have been deleted; remove them
          if (signupToDelete.user_id === userId) {
            removeSignup({id: sheetId, row_id: signupToDelete.id});
          }
        }

        const searchParams = new URLSearchParams();
        searchParams.append('uid', btoa(newUserId || userId));
        localStorage.setItem(`sheet-${sheetId}-user`, newUserId || userId);

        if (scope === 'signup') {
          clearSelectedSignups();
          history.replace(
            {
              pathname: `/${sheetId}/success`,
              search: searchParams.toString(),
            },
            {isEditing}
          );
        }
      } catch (error) {
        evite.error('Failed to save:', error);
        if (typeof error === 'object' && error?.data?.error) {
          setSignupError(error.data.error.data?.detail || {});
        }
      }
    },
    [
      isEditing,
      handleUpdateSignups,
      selectedSignups,
      signupsPendingDeletion,
      handleSave,
      volunteerEmail,
      userId,
      history,
      sheetId,
      clearSelectedSignups,
    ]
  );

  useEffect(() => {
    if (hasAttemptedSave.current && scope === 'signup') {
      validate();
    }
  }, [volunteerName, volunteerEmail, scope]);

  return {onSave};
};
